/**
 * Роли и сетка прав: `GET/POST /admin/roles`, `PUT/DELETE /admin/roles/{id}`.
 *
 * Список модулей для сетки приходит тем же ответом, что и роли, — так экран не
 * расходится с сервером, когда модулей станет больше (R-70).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ApiClient, components } from '@dorixona/api'
import { useApi } from '../../api/context'
import { keys } from '../../api/keys'

type RoleDto = components['schemas']['Role']

export interface Role {
  id: number
  name: string
  /** Системная роль (супер-админ): права только читаются (R-72). */
  system: boolean
  /** Модуль → буквы из «crud»; отсутствие модуля означает «нет доступа». */
  perms: Record<string, string>
  /** Сколько сотрудников на этой роли: по нему запрещаем удаление (R-73). */
  staffCount: number
}

export interface Roles {
  items: Role[]
  /** Колонки сетки прав в порядке, который задаёт сервер. */
  modules: string[]
}

export function toRole(dto: RoleDto): Role {
  return {
    id: dto.id ?? 0,
    name: dto.name ?? '',
    system: dto.is_system ?? false,
    perms: dto.permissions ?? {},
    staffCount: dto.staff_count ?? 0,
  }
}

export async function fetchRoles(api: ApiClient): Promise<Roles> {
  const page = await api.get<{ items?: RoleDto[]; modules?: string[] }>('/admin/roles')
  return { items: (page.items ?? []).map(toRole), modules: page.modules ?? [] }
}

export interface RoleInput {
  id: number
  name: string
  perms: Record<string, string>
}

export async function saveRole(api: ApiClient, role: RoleInput): Promise<Role> {
  const body = { name: role.name, permissions: role.perms }
  const dto = role.id
    ? await api.put<RoleDto>(`/admin/roles/${role.id}`, body)
    : await api.post<RoleDto>('/admin/roles', body)
  return toRole(dto)
}

export function useRoles() {
  const api = useApi()
  return useQuery({ queryKey: keys.roles, queryFn: () => fetchRoles(api) })
}

export function useSaveRole() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (role: RoleInput) => saveRole(api, role),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.roles }),
  })
}

export function useDeleteRole() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.del<unknown>(`/admin/roles/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.roles }),
  })
}
